import React, { FC, useContext, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Button, Checkbox, ITabOption, Tabs } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import notificationService from 'services/notificationService';
import {
  CONNECT_PAYMENT_SERVICES,
  EConnectPaymentService,
  ESettingsAliases,
  GET_CURRENT_BENEFICIARY,
  IConnectPaymentServiceRequest,
  SettingsContext,
  TTypeBeneficiary,
} from 'settings';

import s from '../../TinkoffModal.module.scss';
import {
  Contacts,
  IPResident,
  Requisites,
  Service,
  UlResident,
} from './components';
import { DEFAULT_VALUE } from './constants';
import { ICreateBeneficiaryForm, ICreateBeneficiaryProps } from './interfaces';
import { getRequestData } from './utils';

export const CreateBeneficiary: FC<ICreateBeneficiaryProps> = ({ onClose }) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { me },
  } = useContext(AuthContext);

  const {
    actions: { setSetting },
  } = useContext(SettingsContext);

  const [tab, setTab] = useState<TTypeBeneficiary>('UL_RESIDENT');

  const [connectPaymentService, { loading }] = useMutation<
    boolean,
    IConnectPaymentServiceRequest
  >(CONNECT_PAYMENT_SERVICES, {
    refetchQueries: [GET_CURRENT_BENEFICIARY],
    awaitRefetchQueries: true,
  });

  const TabOption: ITabOption<TTypeBeneficiary>[] = [
    { value: 'UL_RESIDENT', title: 'ООО' },
    { value: 'IP_RESIDENT', title: 'ИП' },
  ];

  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { isDirty },
    setError,
    setValue,
  } = useForm<ICreateBeneficiaryForm>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUE,
  });

  useEffect(() => {
    clearErrors();
  }, [tab]);

  useEffect(() => {
    if (!me) return;

    setValue('phone', me.phone);
  }, [me]);

  const isCheck = watch('check_policy');

  return (
    <form
      onSubmit={handleSubmit((field) => {
        const requestData = getRequestData(field, tab);

        connectPaymentService({
          variables: {
            input: requestData,
            service: EConnectPaymentService.TINKOFF,
          },
        })
          .then(() =>
            notificationService.info({
              t,
              title: t('setting.updated'),
              type: 'success',
              dismiss: 3,
            })
          )
          .then(onClose)
          .then(() => {
            setSetting({
              alias: ESettingsAliases.TINKOFF_SBP_ENABLED,
              value: '1',
            });
          })
          .catch(() =>
            notificationService.info({
              t,
              type: 'error',
              title: t('errors.updated.title'),
              dismiss: 3,
            })
          );
      })}
    >
      <Tabs
        options={TabOption}
        onChange={setTab}
        value={tab}
        className={s.tabs}
      />
      {tab === 'UL_RESIDENT' && (
        <UlResident control={control} setError={setError} />
      )}

      {tab === 'IP_RESIDENT' && (
        <IPResident control={control} setError={setError} />
      )}

      <Contacts control={control} setError={setError} />

      <Requisites control={control} tab={tab} />

      <Service control={control} />

      <div className={s.check_policy}>
        <Controller
          render={({ field }) => <Checkbox {...field} />}
          control={control}
          name="check_policy"
          rules={{
            required: t('errors.required'),
          }}
        />
        <span>
          Я принимаю условия{' '}
          <a
            target="_blank"
            href="https://storage.yandexcloud.net/smartshell-static/legal/oferta-dopuslugi12092024.pdf"
            rel="noreferrer"
          >
            оказания услуг
          </a>{' '}
          и даю согласие на{' '}
          <a
            target="_blank"
            href="https://dl.smartshell.gg/legal/ua-smartshell.pdf"
            rel="noreferrer"
          >
            обработку персональных данных
          </a>
        </span>
      </div>

      <div className={s.footer}>
        <Button
          type="submit"
          fullWidth
          disabled={!isDirty || loading || !isCheck}
        >
          Отправить на проверку
        </Button>
        <span className={s.policy}>{t('policy')}</span>
      </div>
    </form>
  );
};
